import { getSupabaseClient } from "../providers/supabase/supabase";
import { WORKER_URL } from "./enrichFromSource";
import type { EmailAndType, PhoneNumberAndType, Prospect } from "../types";

export type SkipTraceOutcome =
  | "traced" // manual prospect: phones/emails written to Supabase
  | "queued" // source-backed: async skip trace queued on the Airtable record
  | "noMatch" // Endato found nothing
  | "skipped" // not enough info (no owner name or address)
  | "failed";

type TracedPhone = { number: string; type: string; lastReported?: string | null };

const titleCase = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const mergePhones = (
  existing: PhoneNumberAndType[] = [],
  traced: TracedPhone[] = [],
): PhoneNumberAndType[] => {
  const seen = new Set((existing ?? []).map((p) => String(p.number)));
  const out = [...(existing ?? [])];
  for (const t of traced) {
    if (!t.number || seen.has(String(t.number))) continue;
    seen.add(String(t.number));
    // Keep Endato's mobile/landline distinction for display (DB column is jsonb).
    out.push({ number: t.number, type: titleCase(t.type) || "Other" } as PhoneNumberAndType);
  }
  return out;
};

const mergeEmails = (
  existing: EmailAndType[] = [],
  traced: string[] = [],
): EmailAndType[] => {
  const seen = new Set((existing ?? []).map((e) => String(e.email).toLowerCase()));
  const out = [...(existing ?? [])];
  for (const em of traced) {
    if (!em || seen.has(em.toLowerCase())) continue;
    seen.add(em.toLowerCase());
    out.push({ email: em, type: "Other" });
  }
  return out;
};

/**
 * Skip trace one prospect.
 *  - Source-backed (has source_record_id): flag the source Airtable record so
 *    the existing Make/Endato flow runs asynchronously.
 *  - Manual (no source): call the worker's direct Endato endpoint and write the
 *    returned phones/emails straight onto the Supabase prospect.
 */
// Record when a trace ran so the UI can warn before re-tracing.
const stampTraced = async (p: Prospect) => {
  const { error } = await getSupabaseClient()
    .from("prospects")
    .update({ skip_traced_at: new Date().toISOString() })
    .eq("id", p.id);
  if (error) console.error("stampTraced", error);
};

export async function skipTraceOne(p: Prospect): Promise<SkipTraceOutcome> {
  if (p.source_record_id && p.source) {
    const isProbate = /probate/i.test(p.source);
    const endpoint = isProbate ? "/probate-update" : "/auction-update";
    const fields = isProbate
      ? { "Run Skip Trace": true }
      : {
          "Skip Trace Status": "Queued",
          "Skip Trace Queued At": new Date().toISOString(),
        };
    try {
      const r = await fetch(`${WORKER_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recordId: p.source_record_id, fields }),
      });
      if (r.ok) await stampTraced(p);
      return r.ok ? "queued" : "failed";
    } catch {
      return "failed";
    }
  }

  const owner = String(p.owner_name ?? "").trim();
  const parts = owner.split(/\s+/).filter(Boolean);
  if (!parts.length || !p.property_address) return "skipped";

  try {
    const res = await fetch(`${WORKER_URL}/skip-trace`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: parts[0],
        lastName: parts[parts.length - 1],
        address: p.property_address,
        city: p.property_city,
        state: p.property_state || "TX",
        zip: p.property_zip,
      }),
    });
    if (!res.ok) return "failed";
    const d = await res.json();
    if (d.matched && ((d.phones?.length ?? 0) > 0 || (d.emails?.length ?? 0) > 0)) {
      const phones = mergePhones(p.phones, d.phones);
      const emails = mergeEmails(p.emails, d.emails);
      const { error } = await getSupabaseClient()
        .from("prospects")
        .update({ phones, emails, skip_traced_at: new Date().toISOString() })
        .eq("id", p.id);
      return error ? "failed" : "traced";
    }
    // A completed lookup with no match still counts as a trace attempt.
    await stampTraced(p);
    return "noMatch";
  } catch {
    return "failed";
  }
}
